import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { FaRocket, FaUsers, FaChalkboardTeacher, FaRegCalendarAlt, FaLaptop, FaMobileAlt, FaShieldAlt, FaCheck, FaPhone, FaEnvelope } from 'react-icons/fa';
import { RiCustomerServiceFill, RiParentLine } from 'react-icons/ri';
import { IoSchool } from 'react-icons/io5';
import { BsGraphUp, BsCreditCard2Front, BsLightningCharge } from 'react-icons/bs';
import emailjs from '@emailjs/browser';

const SchoolLanding = () => {
  // Get school slug from URL parameters
  const { schoolSlug } = useParams();
  
  // Form state
  const [formData, setFormData] = useState({
    schoolName: '',
    contactName: '',
    email: '',
    phone: '',
    schoolSize: '',
    message: '',
  });

  // Create an effect to handle school slug if present
  useEffect(() => {
    if (schoolSlug) {
      // You can use the slug to fetch specific school data or customize the page
      console.log(`School slug: ${schoolSlug}`);
      // Example: Update document title based on slug
      document.title = `${schoolSlug.charAt(0).toUpperCase() + schoolSlug.slice(1)} School | CyberNest`;
    }
  }, [schoolSlug]);

  // Example school data - in a real app, this would come from an API based on the slug
  const schoolData = {
    'st-marys': {
      name: 'St. Mary\'s School',
      tagline: 'Excellence in Education Since 1965',
      description: 'St. Mary\'s School is committed to providing a nurturing environment where students can excel academically and grow personally.',
      image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1200&q=80',
      stats: {
        established: '1965',
        students: '1,200+',
        teachers: '95',
        campusSize: '25 acres'
      }
    },
    'lincoln-high': {
      name: 'Lincoln High School',
      tagline: 'Preparing Future Leaders',
      description: 'Lincoln High School offers comprehensive education with emphasis on STEM, arts, and athletics to prepare students for college and beyond.',
      image: 'https://images.unsplash.com/photo-1564981797816-1043664bf78d?auto=format&fit=crop&w=1200&q=80',
      stats: {
        established: '1978',
        students: '2,500+',
        teachers: '130',
        campusSize: '40 acres'
      }
    },
    'westfield-academy': {
      name: 'Westfield Academy',
      tagline: 'Where Innovation Meets Tradition',
      description: 'Westfield Academy combines traditional educational values with innovative teaching methods to create well-rounded, confident learners.',
      image: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1200&q=80',
      stats: {
        established: '1992',
        students: '850+',
        teachers: '65',
        campusSize: '15 acres'
      }
    }
  };

  // Get current school data if slug exists and is valid
  const currentSchool = schoolSlug && schoolData[schoolSlug] ? schoolData[schoolSlug] : null;

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    submitting: false,
    error: false,
    message: ''
  });

  // Modal state
  const [showDemoModal, setShowDemoModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus({ 
      submitted: false, 
      submitting: true,
      error: false,
      message: ''
    });
    
    // EmailJS service configuration
    emailjs.send(
      'service_xxxxxxx', // Service ID
      'template_xxxxxxx', // Template ID
      {
        school_name: formData.schoolName,
        contact_name: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        school_size: formData.schoolSize,
        message: formData.message,
      },
      'YOUR_PUBLIC_KEY' // Public Key
    )
      .then((result) => {
        setFormStatus({ 
          submitted: true, 
          submitting: false,
          error: false,
          message: 'Thank you! Our team will contact you shortly to discuss your school website.'
        });
        // Reset form
        setFormData({
          schoolName: '',
          contactName: '',
          email: '',
          phone: '',
          schoolSize: '',
          message: '',
        });
      })
      .catch((error) => {
        setFormStatus({ 
          submitted: false, 
          submitting: false, 
          error: true,
          message: 'There was an error submitting your request. Please try again or contact us directly.'
        });
      });
  };

  // Schools sizes options
  const schoolSizes = [
    { value: '', label: 'Select School Size' },
    { value: 'small', label: 'Small (< 500 students)' },
    { value: 'medium', label: 'Medium (500-1000 students)' },
    { value: 'large', label: 'Large (1000-2000 students)' },
    { value: 'veryLarge', label: 'Very Large (2000+ students)' }
  ];

  // Feature list
  const features = [
    { 
      icon: <FaChalkboardTeacher />, 
      title: "Teacher Portals", 
      description: "Secure access for faculty to post assignments, grades, and communicate with students and parents."
    },
    { 
      icon: <FaUsers />, 
      title: "Student Management", 
      description: "Track attendance, grades, and student progress with our intuitive dashboard systems."
    },
    { 
      icon: <RiParentLine />, 
      title: "Parent Engagement", 
      description: "Keep parents informed with updates about events, assignments, and their child's progress."
    },
    { 
      icon: <FaRegCalendarAlt />, 
      title: "Event Calendar", 
      description: "Interactive school calendar featuring academic events, sports, and extracurricular activities."
    },
    { 
      icon: <IoSchool />, 
      title: "Admissions Portal", 
      description: "Streamline your admissions process with online applications and enrollment tracking."
    },
    { 
      icon: <BsGraphUp />, 
      title: "Analytics Dashboard", 
      description: "Gain insights into website traffic, most visited pages, and user engagement."
    }
  ];

  // Testimonials
  const testimonials = [
    {
      name: "Dr. Sarah Williams",
      position: "Principal, Lincoln High School",
      image: "https://i.pravatar.cc/150?img=32",
      quote: "CyberNest transformed our outdated website into an interactive platform that our students, teachers, and parents love. The admin dashboard saves our staff countless hours!"
    },
    {
      name: "Mark Johnson",
      position: "IT Director, Westfield Academy",
      image: "https://i.pravatar.cc/150?img=53",
      quote: "The security features and performance of our new website are outstanding. The CyberNest team was professional and delivered exactly what we needed on time and within budget."
    },
    {
      name: "Amanda Peters",
      position: "Communications Head, St. Mary's",
      image: "https://i.pravatar.cc/150?img=5",
      quote: "Our enrollment increased by 15% after launching our new website. The modern design and seamless mobile experience have made a significant impact on prospective families."
    }
  ];

  // Plans/pricing
  const plans = [
    {
      name: "Essential",
      price: "₹49,999",
      description: "Perfect for small schools getting started with their online presence",
      features: [
        "Responsive website design",
        "5 custom pages",
        "Events calendar",
        "Contact form",
        "Mobile-friendly design",
        "Social media integration",
        "3 months free maintenance"
      ],
      cta: "Get Started"
    },
    {
      name: "Professional",
      price: "₹79,999",
      description: "Ideal for medium-sized schools needing more advanced features",
      features: [
        "Everything in Essential",
        "10 custom pages",
        "Teacher profiles",
        "Photo galleries",
        "News/blog section",
        "Basic admission form",
        "SEO optimization",
        "6 months free maintenance"
      ],
      cta: "Most Popular",
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "₹129,999",
      description: "Comprehensive solution for large educational institutions",
      features: [
        "Everything in Professional",
        "Unlimited pages",
        "Student/parent portal",
        "Online fee payment",
        "LMS integration",
        "Multi-language support",
        "Custom mobile app",
        "1 year free maintenance"
      ],
      cta: "Contact Us"
    }
  ];

  return (
    <div className="bg-cyber-dark overflow-hidden">
      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="w-full h-full bg-[linear-gradient(90deg,rgba(157,78,221,0.03)_1px,transparent_1px),linear-gradient(0deg,rgba(157,78,221,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
          <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-cyber-dark to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-cyber-dark to-transparent"></div>
          
          {/* Animated particles */}
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className="absolute bg-cyber-pink rounded-full opacity-20 animate-float"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 5}px`,
                height: `${Math.random() * 10 + 5}px`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${Math.random() * 10 + 10}s`
              }}
            ></div>
          ))}
        </div>

        <div className="container mx-auto px-6 pt-32 pb-16 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {currentSchool ? (
                <>
                  <h1 className="text-5xl md:text-6xl font-orbitron font-bold mb-6">
                    <span className="text-white">{currentSchool.name}</span>
                  </h1>
                  <h2 className="text-2xl text-cyber-pink font-orbitron mb-6">
                    {currentSchool.tagline}
                  </h2>
                  <p className="text-xl text-gray-300 mb-8">
                    {currentSchool.description}
                  </p>
                </>
              ) : (
                <>
                  <h1 className="text-5xl md:text-6xl font-orbitron font-bold mb-6">
                    <span className="text-white">Future-Ready</span> <br />
                    <span className="text-cyber-pink">School Websites</span>
                  </h1>
                  <p className="text-xl text-gray-300 mb-8">
                    Elevate your educational institution with a cutting-edge website 
                    that engages students, impresses parents, and streamlines administration.
                  </p>
                </>
              )}
              
              <div className="flex flex-wrap gap-4">
                {currentSchool ? (
                  <>
                    <a 
                      href="#admissions"
                      className="cyber-button"
                    >
                      <span>Apply Now</span>
                    </a>
                    <a 
                      href="#tour"
                      className="cyber-button-outline"
                    >
                      <span>Virtual Tour</span>
                    </a>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => setShowDemoModal(true)}
                      className="cyber-button"
                    >
                      <span>Request Demo</span>
                    </button>
                    <a 
                      href="#plans" 
                      className="cyber-button-outline"
                    >
                      <span>View Pricing</span>
                    </a>
                  </>
                )}
              </div>
              
              {!currentSchool && (
                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <span className="px-3 py-1 bg-cyber-pink/20 border border-cyber-pink rounded-full text-sm text-cyber-pink font-medium">
                    Limited Time Offer
                  </span>
                  <p className="text-cyber-neon text-sm">Get 15% off any package when you sign up before June 30!</p>
                </div>
              )}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="cyber-card p-1 overflow-hidden">
                {currentSchool ? (
                  <img 
                    src={currentSchool.image} 
                    alt={currentSchool.name} 
                    className="w-full h-auto rounded object-cover aspect-video"
                  />
                ) : (
                  <img 
                    src="https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                    alt="Modern school website on devices" 
                    className="w-full h-auto rounded"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-tr from-cyber-dark/80 via-transparent to-transparent rounded"></div>
                
                {!currentSchool && (
                  <>
                    {/* Floating badges */}
                    <div className="absolute top-4 left-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-pink rounded-full text-white flex items-center gap-2">
                      <BsLightningCharge className="text-cyber-pink" />
                      <span>Fast Loading</span>
                    </div>
                    
                    <div className="absolute bottom-4 right-4 px-4 py-2 bg-cyber-dark/80 border border-cyber-neon rounded-full text-white flex items-center gap-2">
                      <FaMobileAlt className="text-cyber-neon" />
                      <span>Mobile Optimized</span>
                    </div>
                  </>
                )}
              </div>
              
              {currentSchool ? (
                <div className="grid grid-cols-2 gap-6 mt-6">
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-pink mb-1">Est. {currentSchool.stats.established}</span>
                    <span className="text-xs text-center text-gray-300">Year Founded</span>
                  </div>
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-neon mb-1">{currentSchool.stats.students}</span>
                    <span className="text-xs text-center text-gray-300">Students</span>
                  </div>
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-purple mb-1">{currentSchool.stats.teachers}</span>
                    <span className="text-xs text-center text-gray-300">Faculty Members</span>
                  </div>
                  <div className="cyber-card p-4 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-cyber-blue mb-1">{currentSchool.stats.campusSize}</span>
                    <span className="text-xs text-center text-gray-300">Campus Size</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Stats floating */}
                  <div className="absolute -bottom-10 -left-10 cyber-card p-4 flex flex-col items-center justify-center w-32 h-32">
                    <span className="text-3xl font-bold text-cyber-neon">98%</span>
                    <span className="text-xs text-center text-gray-300">Parent Satisfaction</span>
                  </div>
                  
                  <div className="absolute -top-5 -right-5 cyber-card p-4 flex flex-col items-center justify-center w-28 h-28">
                    <span className="text-3xl font-bold text-cyber-pink">24/7</span>
                    <span className="text-xs text-center text-gray-300">Technical Support</span>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-12 bg-cyber-dark/80 border-y border-cyber-purple/20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-xl font-orbitron text-gray-400">TRUSTED BY EDUCATIONAL INSTITUTIONS ACROSS INDIA</h2>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center justify-items-center">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity">
                {/* Replace with actual school logos */}
                <div className="h-full w-auto max-w-[120px] bg-gray-500/20 rounded flex items-center justify-center px-4">
                  <span className="font-orbitron text-sm text-gray-400">School {i+1}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              <span className="text-cyber-neon">Features</span> That Transform
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our school websites come packed with features designed specifically for educational institutions.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="cyber-card p-6 hover:shadow-cyber-pink/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="bg-cyber-pink/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 text-cyber-pink text-2xl">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-orbitron font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a href="#contact" className="cyber-button">
              <span>Get These Features</span>
            </a>
          </motion.div>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <section className="py-20 bg-cyber-gray/10 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyber-pink/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyber-neon/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Why <span className="text-cyber-pink">Choose</span> Us
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We understand the unique digital needs of educational institutions.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="bg-cyber-neon/20 p-2 rounded-lg mr-4">
                    <FaLaptop className="text-cyber-neon text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Modern Design</h3>
                    <p className="text-gray-300">
                      Clean and contemporary interfaces that represent your school's brand while providing exceptional user experience.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-pink/20 p-2 rounded-lg mr-4">
                    <FaShieldAlt className="text-cyber-pink text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Secure & Compliant</h3>
                    <p className="text-gray-300">
                      Built with advanced security features to protect sensitive student and institutional data.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-purple/20 p-2 rounded-lg mr-4">
                    <RiCustomerServiceFill className="text-cyber-purple text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Dedicated Support</h3>
                    <p className="text-gray-300">
                      Our technical team is available 24/7 to resolve any issues and help with site management.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-cyber-neon/20 p-2 rounded-lg mr-4">
                    <BsCreditCard2Front className="text-cyber-neon text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-orbitron font-bold mb-2 text-white">Affordable Pricing</h3>
                    <p className="text-gray-300">
                      Flexible packages designed to fit educational budgets of all sizes with no hidden costs.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="cyber-card p-1">
                <img 
                  src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
                  alt="School admin using website dashboard" 
                  className="w-full h-auto rounded"
                />
              </div>
              
              {/* Stats overlay */}
              <div className="absolute -bottom-5 -right-5 bg-cyber-dark border border-cyber-neon p-6 rounded-lg shadow-lg">
                <div className="text-center">
                  <div className="flex justify-between gap-8">
                    <div>
                      <p className="text-3xl font-bold text-cyber-neon">100+</p>
                      <p className="text-sm text-gray-400">Schools Served</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-cyber-pink">99.9%</p>
                      <p className="text-sm text-gray-400">Uptime</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <a href="#testimonials" className="cyber-button-outline">
              <span>See What Schools Say</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-cyber-dark">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              What <span className="text-cyber-pink">Schools</span> Say
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Hear from educational institutions that have transformed their digital presence with us.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="cyber-card p-6 hover:shadow-cyber-neon/20 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name} 
                    className="w-16 h-16 rounded-full border-2 border-cyber-pink mr-4"
                  />
                  <div>
                    <h4 className="text-lg font-orbitron font-bold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-cyber-neon">{testimonial.position}</p>
                  </div>
                </div>
                <p className="text-gray-300">"{testimonial.quote}"</p>
                <div className="mt-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-cyber-pink" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a href="#contact" className="cyber-button">
              <span>Join These Schools</span>
            </a>
          </motion.div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <section id="plans" className="py-20 bg-cyber-gray/10 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyber-pink/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyber-neon/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              <span className="text-cyber-neon">Pricing</span> Plans
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Choose the perfect package for your school's needs and budget.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`cyber-card p-6 flex flex-col h-full relative ${plan.highlighted ? 'border-cyber-pink' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-cyber-pink rounded-full text-white text-sm font-medium">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-2xl font-orbitron font-bold mb-2 text-white">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-3xl font-bold text-cyber-neon">{plan.price}</span>
                  <span className="text-gray-400 ml-1">one-time</span>
                </div>
                <p className="text-gray-300 mb-6">{plan.description}</p>
                
                <div className="flex-grow mb-8">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <FaCheck className="text-cyber-pink mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <a 
                  href="#contact" 
                  className={`${plan.highlighted ? 'cyber-button' : 'cyber-button-outline'} text-center`}
                >
                  <span>{plan.cta}</span>
                </a>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-gray-400 mb-4">Need a custom solution? Contact us for a tailored quote.</p>
            <a href="#contact" className="text-cyber-pink hover:underline">Request Custom Package →</a>
          </motion.div>
        </div>
      </section>
      
      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-cyber-dark relative">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Get <span className="text-cyber-pink">Started</span> Today
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Fill out the form below and our team will contact you within 24 hours to discuss your project.
            </p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto">
            <div className="cyber-card p-8">
              {formStatus.submitted ? (
                <div className="text-center py-8">
                  <div className="bg-cyber-neon/20 border border-cyber-neon text-white p-6 rounded mb-6 inline-block">
                    <svg className="w-16 h-16 mx-auto text-cyber-neon mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <p className="text-xl">{formStatus.message}</p>
                  </div>
                  <button 
                    onClick={() => setFormStatus({submitted: false, submitting: false, error: false, message: ''})}
                    className="cyber-button"
                  >
                    <span>Submit Another Request</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="schoolName" className="block text-white mb-2">School Name <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="text" 
                        id="schoolName"
                        name="schoolName" 
                        value={formData.schoolName}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your school name"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contactName" className="block text-white mb-2">Your Name <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="text" 
                        id="contactName"
                        name="contactName" 
                        value={formData.contactName}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="email" className="block text-white mb-2">Email Address <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="email" 
                        id="email"
                        name="email" 
                        value={formData.email}
                        onChange={handleChange}
                        required 
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your email"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-white mb-2">Phone Number <span className="text-cyber-pink">*</span></label>
                      <input 
                        type="tel" 
                        id="phone"
                        name="phone" 
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="schoolSize" className="block text-white mb-2">School Size <span className="text-cyber-pink">*</span></label>
                    <select
                      id="schoolSize"
                      name="schoolSize"
                      value={formData.schoolSize}
                      onChange={handleChange}
                      required
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                    >
                      {schoolSizes.map(size => (
                        <option key={size.value} value={size.value}>{size.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mb-8">
                    <label htmlFor="message" className="block text-white mb-2">Additional Information</label>
                    <textarea 
                      id="message"
                      name="message" 
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                      placeholder="Tell us about your requirements, timeline, and specific features you're interested in..."
                    ></textarea>
                  </div>
                  
                  {formStatus.error && (
                    <div className="bg-cyber-pink/20 border border-cyber-pink text-white p-4 rounded mb-6">
                      <p>{formStatus.message}</p>
                    </div>
                  )}
                  
                  <div className="flex justify-center">
                    <button 
                      type="submit" 
                      className="cyber-button w-full md:w-auto"
                      disabled={formStatus.submitting}
                    >
                      <span>{formStatus.submitting ? 'Sending...' : 'Get Free Consultation'}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
            
            <div className="mt-12 text-center">
              <p className="text-gray-300 mb-4">Prefer to speak with us directly?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <a href="mailto:ankitmaharshi55@gmail.com" className="text-white hover:text-cyber-neon transition-colors flex items-center gap-2">
                  <FaEnvelope /> ankitmaharshi55@gmail.com
                </a>
                <a href="tel:+919468532536" className="text-white hover:text-cyber-neon transition-colors flex items-center gap-2">
                  <FaPhone /> +91-9468532536
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-20 bg-cyber-gray/10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-orbitron font-bold mb-6">
              Frequently Asked <span className="text-cyber-neon">Questions</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Get answers to common questions about our school website services.
            </p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="cyber-card p-6">
              <h3 className="text-xl font-orbitron font-bold mb-3 text-white">How long does it take to build a school website?</h3>
              <p className="text-gray-300">Our standard timeline is 4-6 weeks from concept to launch, depending on the complexity and features required. We can also accommodate rush projects for an additional fee.</p>
            </div>
            
            <div className="cyber-card p-6">
              <h3 className="text-xl font-orbitron font-bold mb-3 text-white">Do you provide training for our staff?</h3>
              <p className="text-gray-300">Yes, all our packages include comprehensive training sessions for your staff on how to update and manage content on your new website. We also provide detailed documentation for future reference.</p>
            </div>
            
            <div className="cyber-card p-6">
              <h3 className="text-xl font-orbitron font-bold mb-3 text-white">Can we integrate with our existing school management system?</h3>
              <p className="text-gray-300">Absolutely! We specialize in creating seamless integrations with popular school management systems. If you have a custom system, our team can work with your IT department to establish the necessary connections.</p>
            </div>
            
            <div className="cyber-card p-6">
              <h3 className="text-xl font-orbitron font-bold mb-3 text-white">What happens if we need changes after the website is launched?</h3>
              <p className="text-gray-300">We offer flexible maintenance packages to handle ongoing updates and changes. For minor content updates, we provide training so your team can make these changes independently.</p>
            </div>
            
            <div className="cyber-card p-6">
              <h3 className="text-xl font-orbitron font-bold mb-3 text-white">Do you offer hosting services?</h3>
              <p className="text-gray-300">Yes, all our packages include secure, high-speed hosting optimized for educational websites. Our hosting includes regular backups, security monitoring, and technical support.</p>
            </div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <a href="#contact" className="cyber-button-outline">
              <span>Still Have Questions?</span>
            </a>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-cyber-dark border-t border-cyber-purple/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-orbitron font-bold mb-6">
              Ready to <span className="text-cyber-pink">Transform</span> Your School's Online Presence?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Join hundreds of educational institutions across India that have elevated their digital presence with CyberNest.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#contact" className="cyber-button">
                <span>Get Started Now</span>
              </a>
              <button 
                onClick={() => setShowDemoModal(true)}
                className="cyber-button-outline"
              >
                <span>Schedule Demo</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-cyber-dark border-t border-cyber-purple/30 pt-8 pb-6">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-sm text-gray-400">© {new Date().getFullYear()} CyberNest Web Solutions. All rights reserved.</p>
            <p className="text-sm text-gray-500 mt-1">Creating next-generation websites for forward-thinking educational institutions.</p>
            <div className="mt-4">
              <a href="mailto:ankitmaharshi55@gmail.com" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Contact</a>
              <a href="#" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Privacy Policy</a>
              <a href="#" className="text-sm text-gray-400 hover:text-cyber-neon mx-2">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Demo Request Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="cyber-card p-6 max-w-lg w-full relative"
          >
            <button 
              onClick={() => setShowDemoModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <h3 className="text-2xl font-orbitron font-bold mb-4 text-white">Schedule a Free Demo</h3>
            <p className="text-gray-300 mb-6">Fill in your details and our team will schedule a personalized demo of our school website solutions.</p>
            
            <form>
              <div className="mb-4">
                <label htmlFor="demoName" className="block text-white mb-2">Your Name <span className="text-cyber-pink">*</span></label>
                <input 
                  type="text" 
                  id="demoName"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="demoEmail" className="block text-white mb-2">Email Address <span className="text-cyber-pink">*</span></label>
                <input 
                  type="email" 
                  id="demoEmail"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="demoPhone" className="block text-white mb-2">Phone Number <span className="text-cyber-pink">*</span></label>
                <input 
                  type="tel" 
                  id="demoPhone"
                  required 
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="mb-6">
                <label htmlFor="demoDate" className="block text-white mb-2">Preferred Demo Date</label>
                <input 
                  type="date" 
                  id="demoDate"
                  className="w-full bg-cyber-gray/50 border border-cyber-purple/50 rounded p-3 text-white focus:outline-none focus:ring-2 focus:ring-cyber-pink"
                />
              </div>
              
              <div className="flex justify-end">
                <button 
                  type="button"
                  onClick={() => setShowDemoModal(false)} 
                  className="cyber-button-outline mr-3"
                >
                  <span>Cancel</span>
                </button>
                <button type="submit" className="cyber-button">
                  <span>Request Demo</span>
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SchoolLanding; 